import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import { openPdfPreviewGlobal } from '../context/PdfPreviewContext';

const BRAND_COLOR = '#8B1C24';

/**
 * Open PDF in preview modal with Print and Download buttons
 * Uses react-pdf for proper mobile rendering
 * @param {jsPDF} doc - The jsPDF document
 * @param {string} filename - Suggested filename for download
 */
export function openPdfPreview(doc, filename = 'document.pdf') {
  const pdfBlob = doc.output('blob');
  openPdfPreviewGlobal(pdfBlob, filename);
}

/**
 * Generate a barcode as a base64 image
 */
function generateBarcodeImage(value) {
  const canvas = document.createElement('canvas');

  try {
    JsBarcode(canvas, value, {
      format: 'CODE128',
      width: 2,
      height: 50,
      displayValue: true,
      fontSize: 14,
      margin: 10
    });

    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Error generating barcode:', error);
    return null;
  }
}

/**
 * Generate a pallet label PDF
 * @param {Object} palletData - Pallet information
 * @param {string} palletData.pallet_number - Pallet number (e.g., "P0001")
 * @param {string} palletData.created_at - Date created
 * @param {number} palletData.total_weight - Total weight in kg
 * @param {Array} palletData.items - Array of items on the pallet
 * @param {string} palletData.items[].item_name - Item name
 * @param {string} palletData.items[].part_number - Part number
 * @param {number} palletData.items[].quantity - Quantity in cases
 * @param {number} palletData.items[].case_weight - Weight per case in kg
 */
export function generatePalletLabel(palletData) {
  const doc = new jsPDF('p', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  let yPos = 20;

  // Header: "PALLET LABEL"
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRAND_COLOR);
  doc.text('PALLET LABEL', pageWidth / 2, yPos, { align: 'center' });

  yPos += 15;

  // Pallet Number (very large)
  doc.setFontSize(48);
  doc.setTextColor(0, 0, 0);
  doc.text(palletData.pallet_number, pageWidth / 2, yPos, { align: 'center' });

  yPos += 20;

  // Generate barcode (pallet number + lot number combined)
  const barcodeValue = palletData.lot_number
    ? `${palletData.pallet_number}-${palletData.lot_number}`
    : palletData.pallet_number;
  const barcodeImage = generateBarcodeImage(barcodeValue);
  if (barcodeImage) {
    const barcodeWidth = 100;
    const barcodeHeight = 30;
    const barcodeX = (pageWidth - barcodeWidth) / 2;
    doc.addImage(barcodeImage, 'PNG', barcodeX, yPos, barcodeWidth, barcodeHeight);
    yPos += barcodeHeight + 10;
  }

  // Customer Name (prominent)
  if (palletData.customer_name) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(`Customer: ${palletData.customer_name}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
  }

  // Date Created
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  const dateCreated = new Date(palletData.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  doc.text(`Date Created: ${dateCreated}`, pageWidth / 2, yPos, { align: 'center' });

  yPos += 15;

  // Items Table Header
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(BRAND_COLOR);
  doc.setTextColor(255, 255, 255);
  doc.rect(15, yPos - 5, pageWidth - 30, 8, 'F');

  doc.text('Item Name', 20, yPos);
  doc.text('Part #', 105, yPos);
  doc.text('Cases', 145, yPos);
  doc.text('Weight', 175, yPos);

  yPos += 10;

  // Items Table Rows - BIGGER AND BOLDER
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(13);

  palletData.items.forEach((item, index) => {
    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = 20;
    }

    // Alternating row background
    if (index % 2 === 0) {
      doc.setFillColor(240, 240, 240);
      doc.rect(15, yPos - 6, pageWidth - 30, 9, 'F');
    }

    const itemName = item.item_name.length > 30
      ? item.item_name.substring(0, 27) + '...'
      : item.item_name;

    doc.text(itemName, 20, yPos);
    doc.text(item.part_number || 'N/A', 105, yPos);
    doc.text(item.quantity.toString(), 145, yPos);
    const itemWeight = (item.quantity * (item.case_weight || 0)).toFixed(2);
    doc.text(`${itemWeight} kg`, 175, yPos);

    yPos += 9;
  });

  yPos += 5;

  // Total Weight (large, bold)
  doc.setDrawColor(BRAND_COLOR);
  doc.setLineWidth(0.5);
  doc.line(15, yPos, pageWidth - 15, yPos);

  yPos += 8;

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRAND_COLOR);
  doc.text(`TOTAL WEIGHT: ${palletData.total_weight.toFixed(2)} kg`, pageWidth / 2, yPos, { align: 'center' });

  // Footer
  yPos = pageHeight - 15;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Montreal Kosher Bakery', pageWidth / 2, yPos, { align: 'center' });

  // Open PDF in preview with Print/Download buttons
  openPdfPreview(doc, `Pallet-${palletData.pallet_number}.pdf`);
}

/**
 * Generate barcode labels for items (Avery 5160 format)
 * Sheet size: 8.5" x 11" (Letter)
 * Label size: 1" x 2-5/8" (25.4mm x 66.7mm)
 * 30 labels per sheet (3 columns x 10 rows)
 * @param {Array} items - Array of items to print labels for
 * @param {string} items[].name - Item name
 * @param {string} items[].part_number - Part number (used as barcode)
 */
export function generateItemBarcodeLabels(items) {
  const doc = new jsPDF('p', 'mm', 'letter');

  // Avery 5160 dimensions (in mm)
  const LABEL_WIDTH = 66.7;  // 2-5/8"
  const LABEL_HEIGHT = 25.4; // 1"
  const TOP_MARGIN = 12.7;   // 0.5"
  const LEFT_MARGIN = 4.8;   // 3/16"
  const H_SPACING = 3.2;     // 1/8" between columns
  const LABELS_PER_ROW = 3;
  const LABELS_PER_COL = 10;
  const LABELS_PER_PAGE = LABELS_PER_ROW * LABELS_PER_COL;

  items.forEach((item, index) => {
    // Add new page if needed
    if (index > 0 && index % LABELS_PER_PAGE === 0) {
      doc.addPage();
    }

    // Calculate position on current page
    const posOnPage = index % LABELS_PER_PAGE;
    const row = Math.floor(posOnPage / LABELS_PER_ROW);
    const col = posOnPage % LABELS_PER_ROW;

    const x = LEFT_MARGIN + col * (LABEL_WIDTH + H_SPACING);
    const y = TOP_MARGIN + row * LABEL_HEIGHT;

    // Item name (top, truncated if too long)
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);

    let itemName = item.name || 'Unknown Item';
    if (itemName.length > 28) {
      itemName = itemName.substring(0, 25) + '...';
    }
    doc.text(itemName, x + LABEL_WIDTH / 2, y + 5, { align: 'center' });

    // Generate barcode image (middle)
    const barcodeValue = item.part_number || item.barcode || 'N/A';
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, barcodeValue, {
        format: 'CODE128',
        width: 1.5,
        height: 35,
        displayValue: false,
        margin: 0
      });

      const barcodeDataUrl = canvas.toDataURL('image/png');
      const barcodeWidth = 55;
      const barcodeHeight = 12;
      const barcodeX = x + (LABEL_WIDTH - barcodeWidth) / 2;
      doc.addImage(barcodeDataUrl, 'PNG', barcodeX, y + 7, barcodeWidth, barcodeHeight);
    } catch (error) {
      // If barcode generation fails, show text instead
      doc.setFontSize(8);
      doc.text(barcodeValue, x + LABEL_WIDTH / 2, y + 14, { align: 'center' });
    }

    // Part number (bottom)
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(barcodeValue, x + LABEL_WIDTH / 2, y + 22, { align: 'center' });
  });

  // Open PDF in preview with Print/Download buttons
  openPdfPreview(doc, 'Item-Labels.pdf');
}

/**
 * Generate a single barcode label for one item
 * @param {Object} item - Item to print label for
 */
export function generateSingleItemLabel(item) {
  generateItemBarcodeLabels([item]);
}
